import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { MdArrowBackIos } from 'react-icons/md';

import useModal from 'hooks/useModal';
import Button from './ui/Button';

const HEADER_HEIGHT = 54;
const SUBMIT_VIEW_HEIGHT = 72;
const MARGIN = 40;

const Background = styled.div<{ hide?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;

  ${(props) =>
    props.hide && {
      display: 'none',
      zIndex: -1,
    }}
`;

const Container = styled.div<{ $maxWidth: number }>`
  background-color: ${(props) => props.theme.global.bgColor};
  border-radius: 4px;
  width: 100%;
  opacity: 0;
  max-width: ${({ $maxWidth }) => `${$maxWidth}px`};
  display: flex;
  flex-direction: column;
  transform: scale(0.96);
  animation: zoom 0.3s ease-in-out forwards;
  overflow: hidden;
  padding: 24px;

  @keyframes zoom {
    0% {
      transform: scale(0.96);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const Header = styled.div<{ $hasBorder: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  ${(props) =>
    props.$hasBorder && {
      borderBottom: `1px solid ${props.theme.global.borderColor}`,
    }}
`;

const TitleView = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Title = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 18px;
  font-weight: 500;
`;

const PrevButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${(props) => props.theme.global.lightTextColor};
`;

const Form = styled.form``;

const FormContent = styled.div<{ $isFull: boolean }>`
  max-height: ${`calc(100vh - ${HEADER_HEIGHT}px - ${SUBMIT_VIEW_HEIGHT}px - ${MARGIN * 2}px)`};
  height: ${(props) =>
    props.$isFull ? `calc(100vh - ${HEADER_HEIGHT}px - ${SUBMIT_VIEW_HEIGHT}px - ${MARGIN * 2}px)` : 'initial'};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div<{ $isFull: boolean }>`
  max-height: ${`calc(100vh - ${HEADER_HEIGHT}px - ${MARGIN * 2}px)`};
  height: ${(props) => (props.$isFull ? `calc(100vh - ${HEADER_HEIGHT}px - ${MARGIN * 2}px)` : 'initial')};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: relative;
  top: -4px;
  left: 6px;
  font-size: 24px;
  color: ${(props) => props.theme.global.lightTextColor};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SubmitButtonView = styled.div`
  margin-top: 40px;
`;

export const ModalContent = ({ children, isFull = false }: { children: React.ReactNode; isFull?: boolean }) => {
  return <Content $isFull={isFull}>{children}</Content>;
};

interface ModalFormProps {
  isLoading?: boolean;
  isFull?: boolean;
  buttonText: string;
  children: React.ReactNode;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

export const ModalForm = ({ isLoading = false, buttonText, children, isFull = false, onSubmit }: ModalFormProps) => {
  return (
    <Form onSubmit={onSubmit}>
      <FormContent $isFull={isFull}>{children}</FormContent>
      <SubmitButtonView>
        <Button $isFillWidth $height={46} $isLoading={isLoading} style={{ borderRadius: '4px' }}>
          {buttonText}
        </Button>
      </SubmitButtonView>
    </Form>
  );
};

interface ModalHeaderProps {
  children?: React.ReactNode;
  hasBorder?: boolean;
  hasCloseButton?: boolean;
  onCloseClick?: () => void;
  onPrevClick?: () => void;
}

export const ModalHeader = ({
  children,
  hasBorder = false,
  hasCloseButton = true,
  onCloseClick: onPropsCloseClick,
  onPrevClick,
}: ModalHeaderProps) => {
  const modal = useModal();

  const onCloseClick = () => {
    if (onPropsCloseClick) {
      onPropsCloseClick();
    }
    modal.close();
  };

  return (
    <Header $hasBorder={hasBorder}>
      <TitleView>
        {onPrevClick && (
          <PrevButton onClick={onPrevClick}>
            <MdArrowBackIos />
          </PrevButton>
        )}
        {children && <Title>{children}</Title>}
      </TitleView>
      {hasCloseButton && (
        <CloseButton onClick={onCloseClick}>
          <IoClose />
        </CloseButton>
      )}
    </Header>
  );
};

interface ModalProps {
  maxWidth?: number;
  children: React.ReactNode;
}

const Modal = ({ maxWidth = 800, children }: ModalProps) => {
  return (
    <Background>
      <Container $maxWidth={maxWidth}>{children}</Container>
    </Background>
  );
};

export default Modal;
